import { useEffect } from 'react';
import { Modal, Form, Select, App, theme } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useUsersListQuery } from '@/api/auth/queries';
import { useUpdateTenantRoleMutation } from '@/api/auth/mutations';
import type { User } from '@/api/auth/interfaces';
import { STAFF_TENANT_ROLES, TENANT_ROLES, type TenantRoleType } from '@/constants/tenantRoles';
import { useTenantRoleOptions } from '@/hooks/useTenantRoleOptions';
import { ModalTitle } from '@/components/ui/ModalTitle';
import { isAdminEquivalentRole } from '@/utils/authz';
import { formModalWidth } from '@/components/ui/formModalStyles';
import { FormModalFooter } from '@/components/ui/FormModalFooter';

export interface AddClientMemberModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  clientId: string;
  clientName: string;
  /** User IDs already in the client (to exclude from picker or show as disabled) */
  existingMemberIds?: string[];
  /** `customer` adds users with the CUSTOMER role and uses customer wording; defaults to staff members */
  kind?: 'member' | 'customer';
}

export function AddClientMemberModal({
  open,
  onCancel,
  onSuccess,
  clientId,
  clientName,
  existingMemberIds = [],
  kind = 'member'
}: AddClientMemberModalProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const isCustomer = kind === 'customer';
  // Customers always join as CUSTOMER; staff members choose between Owner and Employee
  const allowedRoles: readonly TenantRoleType[] = isCustomer ? [TENANT_ROLES.CUSTOMER] : STAFF_TENANT_ROLES;
  const defaultRole: TenantRoleType = isCustomer ? TENANT_ROLES.CUSTOMER : TENANT_ROLES.EMPLOYEE;
  const roleOptions = useTenantRoleOptions(allowedRoles);
  const texts = isCustomer ? 'tenants.detail.addCustomer' : 'tenants.detail.addMember';
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const { data: usersList = [], isLoading: usersLoading } = useUsersListQuery(open);
  const updateMutation = useUpdateTenantRoleMutation(clientId);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldValue('tenantRole', defaultRole);
    }
  }, [open, form, defaultRole]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await updateMutation.mutateAsync({
        userId: values.userId,
        tenantRole: values.tenantRole as TenantRoleType
      });
      message.success(t(`${texts}.memberAdded`));
      form.resetFields();
      onSuccess?.();
      onCancel();
    } catch (e: unknown) {
      const err = e as { errorFields?: unknown[]; message?: string };
      if (!err?.errorFields) {
        message.error(t(`${texts}.addError`, { error: err?.message || 'Unknown error' }));
      }
    }
  };

  // Global admins/developers reach every tenant through their role and cannot be assigned to one
  const userOptions = (usersList as User[])
    .filter((u) => !existingMemberIds.includes(u.id) && !isAdminEquivalentRole(u.role))
    .map((u) => ({
      value: u.id,
      label: u.name ? `${u.name} (${u.email})` : u.email
    }));

  return (
    <Modal
      title={
        <ModalTitle icon={<UserAddOutlined />} title={t(`${texts}.title`)} />
      }
      open={open}
      footer={null}
      onCancel={onCancel}
      destroyOnHidden
      width={formModalWidth(480)}
    >
      <p style={{ marginBottom: token.marginMD, color: token.colorTextSecondary }}>
        {t(`${texts}.description`, { name: clientName })}
      </p>
      <Form form={form} layout="vertical" initialValues={{ tenantRole: defaultRole }}>
        <Form.Item
          name="userId"
          label={t('tenants.detail.addMember.selectUser', 'User')}
          rules={[{ required: true, message: t('tenants.detail.addMember.selectUserRequired', 'Please select a user') }]}
        >
          <Select
            placeholder={t('tenants.detail.addMember.selectUserPlaceholder', 'Select user')}
            showSearch
            optionFilterProp="label"
            loading={usersLoading}
            options={userOptions}
            notFoundContent={userOptions.length === 0 ? t('tenants.detail.addMember.noUsersAvailable', 'No users available to add') : null}
          />
        </Form.Item>
        <Form.Item
          name="tenantRole"
          label={t('tenants.detail.addMember.role', 'Role')}
          rules={[{ required: true }]}
          hidden={isCustomer}
        >
          <Select options={roleOptions.map(({ value, label }) => ({ value, label }))} />
        </Form.Item>
      </Form>
      <FormModalFooter
        onCancel={onCancel}
        onSubmit={handleSubmit}
        submitLabel={t(`${texts}.add`)}
        loading={updateMutation.isPending}
      />
    </Modal>
  );
}
